<template>
	<Row class="page-container-row always-show-scrollbar">
		<Col :span="24">
			<PageContainer>
				<Row :gutter="[16, 8]">
					<Col :span="24" :offset="0">
						<VerticalLayout>
							<div :class="wrapperClasses">
								<div class="flex-wrapper">
									<TypographyTitle :level="2" class="flex-grow">{{ $t(label + '.header') }}</TypographyTitle>
									<Space v-if="includeImportButtons" :direction="buttonsDirection">
										<Button class="width-100" @click.prevent="onDownloadJson">
											<DownloadOutlined /> {{ $t('base-stations.downloadJsonButtonLabel') }}
										</Button>

										<UploadButton
											:label="$t('base-stations.uploadJsonButtonLabel')"
											action="conf_bridge/upload/start/settings/"
											buttonClass="width-100"
											alwaysShowLabel
											:onSuccess="handleUploadSuccess"
										/>
										<ConfirmImportModal
											v-model="confirmImportModalIsVisible"
											mode="settings"
											:importData="importData"
											:onSuccess="onImportSuccess"
										/>
									</Space>
								</div>
								<Tabs v-if="tabsMode" :activeKey="selectedOption" @change="onChange">
									<TabPane v-for="option in optionsWithLabels" :key="option.value" :tab="option.label" />
								</Tabs>
								<Space v-else>
									<Select
										class="ui-select"
										v-model:value="selectedOption"
										:options="optionsWithLabels"
										:listHeight="selectListHeight"
										@change="onChange"
									/>
									<SubNetworkSelectStoreBound
										v-if="label === 'stats' && selectedOption === 'by-subnetworks'"
										class="ui-select"
										:showLabel="false"
									/>
								</Space>

								<Tabs v-if="showSubOptions" class="pd-md-top" :activeKey="selectedSubOption" @change="onChangeSubOption">
									<TabPane v-for="subOption in subOptionsWithLabels" :key="subOption.value" :tab="subOption.label" />
								</Tabs>
							</div>
							<component :is="contentComponent" />
						</VerticalLayout>
					</Col>
				</Row>
			</PageContainer>
		</Col>
	</Row>
</template>

<script>
import { ref, shallowRef, toRef, computed, watch } from 'vue';
import { TypographyTitle, Row, Col, Select, Tabs, TabPane, Button, Space } from 'ant-design-vue';
import { DownloadOutlined } from '@ant-design/icons-vue';

import { useRoute, useRouter } from 'vue-router';

import Api from '@/api/Api';
import Consts from '@/api/Consts';
import Utils from '@/api/Utils';
import TranslationUtils from '@/i18n/TranslationUtils';

import PageContainer from '@/components/common/PageContainer';
import VerticalLayout from '@/components/common/VerticalLayout';
import UploadButton from '@/components/common/UploadButton';
import ConfirmImportModal from '@/components/common/ConfirmImportModal';

import SubNetworkSelectStoreBound from '@/components/common/controls/SubNetworkSelectStoreBound.vue';

import { useSmallScreen } from '@/utils/useSmallScreen';

export default {
	components: {
		TypographyTitle, Row, Col, Select, Tabs, TabPane, Button, Space,
		DownloadOutlined,
		PageContainer, VerticalLayout, UploadButton,
		ConfirmImportModal,
		SubNetworkSelectStoreBound,
	},
	props: {
		label: {
			type: String,
			required: true,
		},
		routes: {
			type: Array,
			required: true,
		},
		mode: {
			type: String,
			default: 'select',
		},
		includeImportButtons: {
			type: Boolean,
			default: false,
		},
	},
	setup(props) {
		const routesRef = toRef(props, 'routes');
		const modeRef = toRef(props, 'mode');
		const route = useRoute();
		const selectedItem = shallowRef();
		const selectedSubItem = shallowRef();

		const syncSelectionFromRoute = () => {
			const newSelectedItem = Utils.findItem(
				routesRef.value,
				route.params?.id,
				'value',
				routesRef.value[0],
			);
			selectedItem.value = newSelectedItem;
			const tabs = newSelectedItem.tabs != null ? newSelectedItem.tabs : [];
			if (tabs.length > 0) {
				selectedSubItem.value = Utils.findItem(
					tabs,
					route.params?.subId,
					'value',
					tabs[0],
				);
			} else {
				selectedSubItem.value = null;
			}
		};
		syncSelectionFromRoute();

		const selectedOption = computed(() => selectedItem.value.value);
		const selectedOptionTabs = computed(() => selectedItem.value.tabs != null ? selectedItem.value.tabs : []);
		const selectedSubOption = computed(() => selectedSubItem.value != null ? selectedSubItem.value.value : null);
		const contentComponent = computed(() => selectedSubItem.value != null ? selectedSubItem.value.component : selectedItem.value.component);
		const optionsWithLabels = TranslationUtils.addTranslatedField(routesRef, 'value', 'label', props.label + '.options');
		const subOptionsWithLabels = TranslationUtils.addTranslatedField(selectedOptionTabs, 'value', 'label', props.label + '.options');
		const showSubOptions = computed(() => subOptionsWithLabels.value.length > 0);

		const router = useRouter();
		const onChange = value => {
			router.push({
				name: props.label + '-type',
				params: { id: value },
			});
		};
		const onChangeSubOption = value => {
			router.push({
				name: props.label + '-type',
				params: { id: selectedOption.value, subId: value },
			});
		};
		const SELECT_OPTION_HEIGHT_PX = 32;
		const SELECT_MAX_VISIBLE_OPTIONS = 15;
		const selectListHeight = SELECT_OPTION_HEIGHT_PX * SELECT_MAX_VISIBLE_OPTIONS;
		const tabsMode = computed(() => modeRef.value === 'tabs');
		const wrapperClasses = computed(() => {
			let classes = [];
			if (tabsMode.value) {
				classes.push('top-panel-tabs');
			} else {
				classes.push('top-panel-select');
			}
			if (showSubOptions.value) {
				classes.push('top-panel-sub-options');
			}
			return classes;
		});
		const { smallScreen } = useSmallScreen();
		const buttonsDirection = computed(() => smallScreen.value ? 'vertical' : 'horizontal');

		const onDownloadJson = () => Api.confBridgeDownloadJson();
		// import modal
		const confirmImportModalIsVisible = ref(false);
		const importData = ref({});
		const handleUploadSuccess = response => {
			confirmImportModalIsVisible.value = true;
			importData.value = response;
		}
		const onImportSuccess = () => {
			window.location.reload(true);
		};
		// один раз фиксируем изменения, на случай если подгрузились права пользователя, и routes изменились
		watch(
			routesRef,
			syncSelectionFromRoute,
			{ once: true },
		);
		// вручную синхронизируем выбор при изменении параметров в URL, потому что теперь вся страница не перемонтируется
		watch(
			() => [route.params.id, route.params.subId],
			syncSelectionFromRoute,
		);
		
		return {
			selectedOption,
			selectedOptionTabs,
			selectedSubOption,
			contentComponent,
			optionsWithLabels,
			subOptionsWithLabels,
			showSubOptions,
			onChange,
			onChangeSubOption,
			wrapperClasses,
			buttonsDirection,
			onDownloadJson,
			handleUploadSuccess,
			confirmImportModalIsVisible,
			importData,
			onImportSuccess,

			tabsMode,
			selectListHeight,
			borderColor: ref(Consts.COLORS.default),
		};
	},
}
</script>

<style scoped>
.ui-select {
	width: 300px;
}

.top-panel-select {
	padding: 24px;
	background-color: #fafafa;
	border-block-end: 1px solid v-bind(borderColor);
}

.top-panel-tabs {
	padding-block-start: 12px;
	padding-block-end: 0;
	padding-inline-start: 12px;
	background-color: white;
}

.top-panel-sub-options {
	padding-block-end: 0;
}

.always-show-scrollbar {
	min-height: 100vh;
}
</style>
<style>
.top-panel-sub-options .ant-tabs-top > .ant-tabs-nav {
	margin: 0;
}
</style>
